import { FC, memo, useMemo } from 'react';
import { getDomain } from 'shared/helpers/getDomain';
import cls from './score-card.module.scss';
import { ProgressBar } from 'shared/ui/progress-bar/progress-bar';
import { ScoreSquare } from 'shared/ui/score-square/score-square';
import { Icon } from 'shared/ui/icon/icon';
import { ICONS } from 'shared/constants/images';
import { track } from 'utils/mixpanel';
import { VisibilityEntityBase } from 'store/api/apiTypes';

export interface CompetitorRowProps {
  competitor: VisibilityEntityBase;
  maxScore: number;
  currentDomain?: string;
  total: number;
  onSelect?: (c?: VisibilityEntityBase) => void;
  className?: string;
  isVertical?: boolean;
  isSelected?: boolean;
  isTrialExpired?: boolean;
  restricted?: boolean;
  isTrial?: boolean;
  productNumber?: number;
  competitorNumber: number;
}

export const CompetitorRow: FC<CompetitorRowProps> = memo(
  ({
    competitor,
    currentDomain,
    total,
    onSelect,
    className,
    productNumber,
    competitorNumber,
    isSelected = false,
    isVertical = false,
    isTrialExpired = false,
    isTrial = false,
    restricted = false,
  }) => {
    const isCurrent = competitor.domain === currentDomain;
    const isClickable = total > 2 && !restricted;
    const progressClass = useMemo(() => {
      if (competitor.mention_rate <= 10) {
        return `${cls.progressBar} ${isCurrent ? cls.current : competitor.mention_rate >= 8 ? cls.scoreHigh : competitor.mention_rate >= 4 ? cls.scoreMedium : cls.scoreLow}`;
      }
      return `${cls.progressBar} ${competitor.mention_rate >= 80 ? cls.scoreHigh : competitor.mention_rate >= 40 ? cls.scoreMedium : cls.scoreLow}`;
    }, [competitor.mention_rate, isCurrent]);

    const handleSelect = (competitor: VisibilityEntityBase) => {
      if (isCurrent || isSelected) {
        track('AIS:PROD_COMPETITORS-COMPETITOR-CLOSED', {
          product: productNumber,
          competitor_number: competitorNumber,
          is_trial: isTrial,
        });
        onSelect?.(undefined);
        return;
      } else {
        track('AIS:PROD_COMPETITORS-COMPETITOR-OPENED', {
          product: productNumber,
          competitor_number: competitorNumber,
          is_trial: isTrial,
        });
        onSelect?.(competitor);
        return;
      }
    };

    if (isVertical) {
      return (
        <div
          key={competitor.domain}
          className={`${cls.competitorVertical} ${isClickable ? cls.clickable : ''} ${className || ''}`}
          onClick={isClickable ? () => onSelect?.(competitor) : undefined}
        >
          <div className={cls.competitorHeader}>
            <span className={cls.competitorName}>
              {getDomain(competitor.domain)}
            </span>
            <span className={cls.competitorScore} style={{ width: 'auto' }}>
              {competitor.mention_rate.toFixed()}%
            </span>
          </div>
          <div className={cls.competitorProgress}>
            <ProgressBar
              progressClass={progressClass}
              score={competitor.mention_rate}
            />
          </div>
        </div>
      );
    }

    return (
      <div
        key={competitor.domain}
        className={`${cls.competitor} ${isClickable && !isCurrent ? cls.clickable : ''} ${
          isSelected ? cls.selected : ''
        }`}
        onClick={
          isClickable || isCurrent ? () => handleSelect(competitor) : undefined
        }
      >
        <div className={cls.competitorInfo}>
          <span className={cls.competitorName}>
            {getDomain(competitor.domain)}
          </span>
          {restricted && !isCurrent && (
            <Icon pathToIcon={ICONS.lockGray} size={16} />
          )}
        </div>
        <div className={cls.competitorProgress}>
          {competitor.mention_rate !== undefined && (
            <ScoreSquare
              score={competitor.mention_rate}
              isSmall
              isLocked={isTrialExpired}
            />
          )}
        </div>
      </div>
    );
  }
);

CompetitorRow.displayName = 'CompetitorRow';
